/**
 * **房间配置的保存 / 导出 / 导入。**
 *
 * 只存**和官方默认不同的字段**（diff）。这样：
 * - 玩家数变了，下一次进房不会被旧存档钉死；
 * - 以后官方默认值改了，没动过的字段会自动跟上。
 *
 * 纯函数 + 一个注入式的存储：diff / 序列化 / 解析都能在 node 里断言。
 */
import { z } from 'zod';
import { t } from '../i18n';
import {
  officialConfig,
  type ConfigPatch,
  type PlayerCount,
  type RoomConfig,
} from '../core/config';
import type { CardRegistry, DeckCounts } from '../core/pack';
import type { StorageLike } from './settings';

/** 落盘用的键。带版本号，将来改结构时可以并存读旧键做迁移 */
export const ROOM_PRESET_STORAGE_KEY = 'ivory-roulette/room-preset';
export const ROOM_PRESET_VERSION = 1;
/** 导出文件里用来认自己人的标记 */
export const ROOM_PRESET_KIND = 'ivory-roulette/room-preset';

export interface RoomPresetStore {
  load(): ConfigPatch | null;
  save(patch: ConfigPatch): void;
}

function sameCounts(a: DeckCounts, b: DeckCounts): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? 0) !== (b[key] ?? 0)) return false;
  }
  return true;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

function officialBaseline(config: RoomConfig, registry: CardRegistry): RoomConfig {
  // ⚠️ playerCount 在编辑过程中可以是非法值；越界就按 2 人局取基线，
  //    别让"导出一份配置"因为一个坏数字崩掉。
  const count = (
    config.playerCount >= 2 && config.playerCount <= 5 ? config.playerCount : 2
  ) as PlayerCount;
  return officialConfig(count, registry);
}

/**
 * **当前配置相对官方默认的差集。** 空对象 = 全是默认。
 *
 * ⚠️ 只比较**配置字段**，不比较派生出来的发牌计划。
 */
export function roomConfigDiff(config: RoomConfig, registry: CardRegistry): ConfigPatch {
  const base = officialBaseline(config, registry);
  // ConfigPatch 的字段全是 readonly（对外是描述），这里要逐字段累加，
  // 所以先在一个去掉 readonly 的本地类型上写。
  type MutablePatch = { -readonly [K in keyof ConfigPatch]: ConfigPatch[K] };
  const patch: MutablePatch = {};

  if (config.openingHandSize !== base.openingHandSize) patch.openingHandSize = config.openingHandSize;
  if (config.openingRescueCount !== base.openingRescueCount) {
    patch.openingRescueCount = config.openingRescueCount;
  }
  if (config.lethalInDeck !== base.lethalInDeck) patch.lethalInDeck = config.lethalInDeck;
  if (config.extraRescueInDeck !== base.extraRescueInDeck) {
    patch.extraRescueInDeck = config.extraRescueInDeck;
  }
  if (config.deadPlayersCanNope !== base.deadPlayersCanNope) {
    patch.deadPlayersCanNope = config.deadPlayersCanNope;
  }
  if (config.firstPlayer !== base.firstPlayer) patch.firstPlayer = config.firstPlayer;
  if (!sameList(config.enabledPacks, base.enabledPacks)) {
    patch.enabledPacks = [...config.enabledPacks];
  }
  if (!sameCounts(config.deck, base.deck)) patch.deck = { ...config.deck };

  const combos: {
    twoOfAKind?: boolean;
    threeOfAKind?: boolean;
    fiveDifferent?: boolean;
    fiveDifferentCanTakeDefuse?: boolean;
  } = {};
  if (config.combos.twoOfAKind !== base.combos.twoOfAKind) combos.twoOfAKind = config.combos.twoOfAKind;
  if (config.combos.threeOfAKind !== base.combos.threeOfAKind) {
    combos.threeOfAKind = config.combos.threeOfAKind;
  }
  if (config.combos.fiveDifferent !== base.combos.fiveDifferent) {
    combos.fiveDifferent = config.combos.fiveDifferent;
  }
  if (config.combos.fiveDifferentCanTakeDefuse !== base.combos.fiveDifferentCanTakeDefuse) {
    combos.fiveDifferentCanTakeDefuse = config.combos.fiveDifferentCanTakeDefuse;
  }
  if (Object.keys(combos).length > 0) patch.combos = combos;

  return patch;
}

/** 导出文件的文本。带 kind + 版本号，导入时认得出、也顶得住未来迁移 */
export function serializeRoomPreset(config: ConfigPatch): string {
  return JSON.stringify({ kind: ROOM_PRESET_KIND, v: ROOM_PRESET_VERSION, config }, null, 2) + '\n';
}

const DeckSchema = z.record(z.string(), z.number().int());
const CombosSchema = z.object({
  twoOfAKind: z.boolean().optional(),
  threeOfAKind: z.boolean().optional(),
  fiveDifferent: z.boolean().optional(),
  fiveDifferentCanTakeDefuse: z.boolean().optional(),
});
const ConfigPatchSchema = z.object({
  openingHandSize: z.number().int().optional(),
  openingRescueCount: z.number().int().optional(),
  lethalInDeck: z.number().int().nullable().optional(),
  extraRescueInDeck: z.number().int().nullable().optional(),
  deadPlayersCanNope: z.boolean().optional(),
  firstPlayer: z.string().optional(),
  enabledPacks: z.array(z.string()).optional(),
  deck: DeckSchema.optional(),
  combos: CombosSchema.optional(),
});
const RoomPresetFileSchema = z.object({
  kind: z.literal(ROOM_PRESET_KIND),
  v: z.number().int(),
  config: ConfigPatchSchema,
});

export type ParsedRoomPreset =
  | { readonly ok: true; readonly patch: ConfigPatch }
  | { readonly ok: false; readonly reason: string };

/**
 * 解析一份导入的配置。**不抛**，失败给人话原因（直接显示在房间页上）。
 *
 * ⚠️ 未知字段被 zod 丢掉；这里只认导出的那份形状，别的一律当成"不是配置"。
 */
export function parseRoomPreset(text: string): ParsedRoomPreset {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: t('preset.invalidJson') };
  }
  const parsed = RoomPresetFileSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, reason: t('preset.wrongKind') };
  if (parsed.data.v > ROOM_PRESET_VERSION) {
    return { ok: false, reason: t('preset.tooNew') };
  }
  return { ok: true, patch: parsed.data.config as unknown as ConfigPatch };
}

/** 包一层 localStorage。任何访问都可能抛（隐私模式），跟设置一样兜住 */
export function createRoomPresetStore(
  storage: StorageLike,
  key = ROOM_PRESET_STORAGE_KEY,
): RoomPresetStore {
  let fallback: string | null = null;
  return {
    load() {
      let text: string | null;
      try {
        text = storage.getItem(key) ?? fallback;
      } catch {
        text = fallback;
      }
      if (typeof text !== 'string' || text.trim() === '') return null;
      const parsed = parseRoomPreset(text);
      return parsed.ok ? parsed.patch : null;
    },
    save(patch) {
      const text = serializeRoomPreset(patch);
      fallback = text;
      try {
        storage.setItem(key, text);
      } catch {
        // 存不下就算了，fallback 已经拿到这一份
      }
    },
  };
}
